import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useContentId } from '@/components/SlugGate';
import { sanitizeHtml } from '@/lib/sanitize';
import Navbar from '@/components/Navbar';
import { formatReadingTime } from '@/components/ReadingTime';
import ShareButtons from '@/components/ShareButtons';
import SEOHead from '@/components/SEOHead';
import { articleJsonLd } from '@/lib/jsonLd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  Clock, 
  Eye,
  Heart,
  BookOpen,
  Loader2,
  Sparkles,
  ArrowLeft,
  Calendar,
  Newspaper,
  ChevronRight,
  Lock,
  Compass,
  ArrowRight
} from 'lucide-react';
import { PremiumCardOverlay, PremiumGate, PremiumTeaser } from '@/components/PremiumLock';
import CoverImage from '@/components/CoverImage';

function ArticleCard({ article, onSelect }) {
  return (
    <Card 
      className="bg-surface border-border overflow-hidden cursor-pointer group transition-all duration-300 hover:border-ruby/50 hover:shadow-lg hover:shadow-ruby/10"
      onClick={() => onSelect(article.slug || article.id)}
      data-testid={`article-card-${article.id}`}
    >
      <div className="relative aspect-video overflow-hidden bg-surface">
        <CoverImage
          src={article.cover_image}
          alt={article.title}
          Icon={Newspaper}
          className="transition-transform duration-500 group-hover:scale-110"
          testId={`article-card-image-${article.id}`}
        />
        <Badge className="absolute top-3 left-3 z-20 bg-ruby/90 text-white">
          {article.category_name}
        </Badge>
        {article.is_locked && (
          <>
            <Badge className="absolute top-3 right-3 z-20 bg-gradient-to-r from-gold to-gold-light text-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Premium
            </Badge>
            <PremiumCardOverlay />
          </>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-ruby transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{formatReadingTime(article.reading_time)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            <span>{article.views}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GuideBanner({ blocks }) {
  if (!blocks) return null;
  const enabled = String(blocks['prazer.guide_banner.enabled'] ?? 'true').toLowerCase().trim() !== 'false';
  if (!enabled) return null;

  const title = blocks['prazer.guide_banner.title'] || 'Vai mais fundo. Conhece-te.';
  const subtitle = blocks['prazer.guide_banner.subtitle'] || 'Os artigos abrem a porta — os guias "Conhece-te" levam-te lá dentro.';
  const cta = blocks['prazer.guide_banner.cta'] || 'Descobrir os Guias';
  const image = blocks['prazer.guide_banner.image'] || '';
  const link = blocks['prazer.guide_banner.link'] || '/conhece-te';
  const isExternal = /^https?:\/\//i.test(link);

  return (
    <a
      href={link}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      data-testid="prazer-guide-banner"
      className="group block my-12 rounded-2xl overflow-hidden border border-ruby/30 bg-gradient-to-br from-ruby/15 via-background to-black hover:border-ruby/60 hover:shadow-lg hover:shadow-ruby/20 transition-all"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-6 p-6 md:p-8 relative">
        {/* Decorative glow */}
        <div className="absolute -top-16 -right-16 w-48 h-48 bg-ruby/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-ruby/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex items-start gap-4">
          {image ? (
            <img src={image} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover shrink-0 border border-ruby/30" />
          ) : (
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl shrink-0 bg-ruby/20 flex items-center justify-center border border-ruby/30">
              <Compass className="w-8 h-8 md:w-10 md:h-10 text-ruby" />
            </div>
          )}
          <div className="min-w-0">
            <Badge className="mb-2 bg-ruby/20 text-ruby border-ruby/40 hover:bg-ruby/30">Conhece-te</Badge>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2 leading-tight">{title}</h3>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{subtitle}</p>
          </div>
        </div>

        <div className="relative shrink-0">
          <Button
            type="button"
            tabIndex={-1}
            className="bg-ruby hover:bg-ruby-light text-white px-6 py-5 group-hover:translate-x-1 transition-transform pointer-events-none"
          >
            {cta}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </a>
  );
}

function ArticleDetailView({ articleId, onBack }) {
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [cmsBlocks, setCmsBlocks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/articles/' + articleId);
        setArticle(res.data);
        const relRes = await api.get('/articles?category=' + res.data.category + '&limit=4');
        setRelatedArticles(relRes.data.filter(a => a.id !== articleId).slice(0, 3));
        // Fetch CMS blocks for the +Prazer page (banner). Public endpoint, auto-seeds defaults.
        try {
          const cmsRes = await api.get('/cms/content/prazer');
          setCmsBlocks(cmsRes.data || {});
        } catch (e) { /* non-blocking */ }
        // Award points (backend rejects anonymous calls — harmless to attempt)
        if (!res.data.is_locked) {
          try {
            await api.post('/points/earn/read_article?reference_id=' + articleId);
          } catch (e) { /* ignore duplicate or auth errors */ }
        }
      } catch (e) {
        toast.error('Artigo não encontrado');
        onBack();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [articleId, onBack]);

  const formatDate = (d) => new Date(d).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  if (!article) return null;

  if (article.is_locked) {
    return (
      <div>
        <SEOHead
          title={article.title}
          description={article.excerpt}
          image={article.cover_image}
          jsonLd={articleJsonLd(article)}
        />
        <div className="relative h-64 md:h-80 overflow-hidden rounded-xl mb-8">
          <CoverImage src={article.cover_image} alt={article.title} Icon={Newspaper} loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          <div className="absolute top-6 left-6">
            <Button variant="ghost" onClick={onBack} className="bg-black/50 hover:bg-black/70 text-white" data-testid="back-to-articles">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-ruby text-white">{article.category_name}</Badge>
            <span className="text-muted-foreground text-sm flex items-center gap-1">
              <Clock className="w-3 h-3" />{formatReadingTime(article.reading_time)} de leitura
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4" data-testid="article-title">{article.title}</h1>
          <p className="text-lg text-muted-foreground">{article.excerpt}</p>
        </div>

        <PremiumTeaser
          teaserHtml={article.content}
          title="Continua a ler com Premium"
          message="Está a ver a prévia deste artigo. Assine Premium para desbloquear o conteúdo completo — e todos os outros artigos, contos e confissões da Intimate.pt."
        />
      </div>
    );
  }

  return (
    <div>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        image={article.cover_image}
        jsonLd={articleJsonLd(article)}
      />
      <div className="relative h-64 md:h-80 overflow-hidden rounded-xl mb-8">
        <CoverImage src={article.cover_image} alt={article.title} Icon={Newspaper} loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="absolute top-6 left-6">
          <Button variant="ghost" onClick={onBack} className="bg-black/50 hover:bg-black/70 text-white" data-testid="back-to-articles">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Badge className="bg-ruby text-white">{article.category_name}</Badge>
          <span className="text-muted-foreground text-sm flex items-center gap-1">
            <Clock className="w-3 h-3" />{formatReadingTime(article.reading_time)} de leitura
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4" data-testid="article-title">{article.title}</h1>
        <p className="text-lg text-muted-foreground mb-6">{article.excerpt}</p>
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(article.created_at)}</span>
            <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{article.views} visualizações</span>
          </div>
          <ShareButtons
            url={`${window.location.origin}/prazer/${article.slug || article.id}`}
            title={`${article.title} - Intimate.pt`}
            description={article.excerpt?.substring(0, 150) || ''}
            variant="icon"
            size="sm"
          />
        </div>
      </div>

      <article className="mb-12" data-testid="article-content">
        <div className="text-muted-foreground reader-content max-w-none" style={{ lineHeight: '1.9' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content) }} />
      </article>

      {article.tags && article.tags.length > 0 && (
        <div className="mb-12">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Etiquetas</h3>
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag, i) => (
              <Badge key={i} variant="outline" className="border-border text-muted-foreground">#{tag}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Editable banner — between article end and related articles */}
      <GuideBanner blocks={cmsBlocks} />

      {relatedArticles.length > 0 && (
        <div className="border-t border-border pt-8">
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-ruby" />Artigos Relacionados
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {relatedArticles.map((rel) => (
              <ArticleCard key={rel.id} article={rel} onSelect={() => navigate('/prazer/' + (rel.slug || rel.id))} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MaisPrazer() {
  const navigate = useNavigate();
  const articleId = useContentId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const PAGE_SIZE = 12;
  
  const selectedCategory = searchParams.get('categoria') || '';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const query = selectedCategory ? `?category=${selectedCategory}&limit=${PAGE_SIZE}` : `?limit=${PAGE_SIZE}`;
      const [articlesRes, categoriesRes, featuredRes] = await Promise.all([
        api.get('/articles' + query),
        api.get('/prazer/categories'),
        api.get('/articles/featured?limit=3')
      ]);
      setArticles(articlesRes.data);
      setHasMore(articlesRes.data.length >= PAGE_SIZE);
      setCategories(categoriesRes.data);
      setFeaturedArticles(featuredRes.data);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const query = selectedCategory 
        ? `?category=${selectedCategory}&limit=${PAGE_SIZE}&skip=${articles.length}`
        : `?limit=${PAGE_SIZE}&skip=${articles.length}`;
      const res = await api.get('/articles' + query);
      setArticles(prev => [...prev, ...res.data]);
      setHasMore(res.data.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Error loading more:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!articleId) {
      loadData();
    }
  }, [articleId, loadData]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadData();
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/articles?search=' + encodeURIComponent(searchTerm) + '&limit=100');
      setArticles(response.data);
      setHasMore(false);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    if (category === selectedCategory) {
      setSearchParams({});
    } else {
      setSearchParams({ categoria: category });
    }
    setSearchTerm('');
  };

  const handleSelectArticle = (id) => {
    navigate('/prazer/' + id);
  };

  const handleBackToList = () => {
    navigate('/prazer');
  };

  // Show article detail if articleId is present
  if (articleId) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            <ArticleDetailView articleId={articleId} onBack={handleBackToList} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <SEOHead
        title="+ Prazer"
        description="Artigos sobre sexualidade, prazer, relações e bem-estar íntimo para adultos."
        type="website"
      />
      
      <main className="pt-20 pb-12">
        {/* Hero Section */}
        <div className="relative py-16 px-6 mb-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-ruby/20 via-transparent to-ruby/10" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=1920')] bg-cover bg-center opacity-10" />
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-ruby/20 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-ruby" />
              <span className="text-ruby font-medium">Artigos Educativos</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              + <span className="text-ruby">Prazer</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Artigos sobre intimidade, relacionamentos e bem-estar sexual. 
              Conhecimento para uma vida mais plena e satisfatória.
            </p>
            
            {/* Search */}
            <div className="flex max-w-md mx-auto gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar artigos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 bg-surface border-border"
                  data-testid="article-search-input"
                />
              </div>
              <Button 
                onClick={handleSearch}
                className="bg-ruby hover:bg-ruby-light"
                data-testid="article-search-btn"
              >
                Pesquisar
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-8">
            <Button
              variant={!selectedCategory ? "default" : "outline"}
              onClick={() => handleCategoryChange('')}
              className={!selectedCategory ? "bg-ruby hover:bg-ruby-light" : "border-border"}
              data-testid="category-all"
            >
              Todos
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.slug}
                variant={selectedCategory === cat.slug ? "default" : "outline"}
                onClick={() => handleCategoryChange(cat.slug)}
                className={selectedCategory === cat.slug ? "bg-ruby hover:bg-ruby-light" : "border-border"}
                data-testid={`category-${cat.slug}`}
              >
                {cat.name}
                <Badge variant="secondary" className="ml-2 bg-white/10">
                  {cat.article_count}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Featured Articles */}
          {!selectedCategory && !searchTerm && featuredArticles.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Heart className="w-6 h-6 text-ruby" />
                Em Destaque
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} onSelect={handleSelectArticle} />
                ))}
              </div>
            </div>
          )}

          {/* All Articles */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-ruby" />
              {selectedCategory 
                ? categories.find(c => c.slug === selectedCategory)?.name || 'Artigos'
                : 'Todos os Artigos'
              }
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-ruby" />
              </div>
            ) : articles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} onSelect={handleSelectArticle} />
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center mt-10">
                    <Button
                      onClick={loadMore}
                      disabled={loadingMore}
                      variant="outline"
                      className="border-ruby text-ruby hover:bg-ruby/10 px-8"
                      data-testid="load-more-articles"
                    >
                      {loadingMore ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      {loadingMore ? 'A carregar...' : 'Carregar mais artigos'}
                    </Button>
                  </div>
                )}
                {!hasMore && articles.length > PAGE_SIZE - 1 && (
                  <p className="text-center text-sm text-muted-foreground mt-8">
                    A mostrar todos os {articles.length} artigos
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <BookOpen className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Nenhum artigo encontrado
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm 
                    ? `Não encontrámos resultados para "${searchTerm}"`
                    : 'Ainda não há artigos nesta categoria'
                  }
                </p>
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setSearchParams({});
                    loadData();
                  }}
                  className="bg-ruby hover:bg-ruby-light"
                >
                  Ver Todos os Artigos
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
